import Vue from 'vue'

/**
 * Le module Firmware (ZEborne seul) : les firmwares des constructeurs, les mises à jour (seules ou en campagne),
 * l'épreuve, les fiches de test et les diagnostics des bornes. Les constructeurs : ManufacturerRepository.
 */
class FirmwareRepository {

  // Les bornes qui ont installé un firmware, et celles de ses modèles qui pourraient le recevoir
  // → { installed, chargingPoints: [{ chargingPointId, name, identifier, locationName, model, connected }], compatibleIds }
  firmwareChargingPoints(versionId) {
    return this.fetch({ url: `/api/firmware/versions/${versionId}/charging-points`, method: 'get' })
  }

  // Les bornes qu'un chiffre de la fiche désigne, ou un périmètre choisi :
  // { manufacturerId, model?, state?, organizationId?, locationId?, version? } → { chargingPointIds }
  selection(filters) {
    return this.fetch({ url: '/api/firmware/selection', method: 'get', data: filters })
  }

  // La page de lancement : les firmwares qu'on peut viser pour ces bornes, et le chemin de chacune
  plan(chargingPointIds, versionId = null) {
    return this.fetch({ url: '/api/firmware/plan', method: 'post', data: { chargingPointIds, versionId } })
  }

  // { chargingPointIds, versionId, pace, failureStopsSite, pauseAfterFailures, startAt, windowStart, windowEnd, reason } → { id }
  startCampaign(campaign) {
    return this.fetch({ url: '/api/firmware/campaigns', method: 'post', data: campaign })
  }

  // Par pages de 50, la plus récente d'abord
  // Les mises à jour d'une borne seule (hors campagne) : { updates, more }
  singleUpdates(offset = 0) {
    return this.fetch({ url: '/api/firmware/single-updates', method: 'get', data: { offset } })
  }

  campaigns(offset = 0) {
    return this.fetch({ url: '/api/firmware/campaigns', method: 'get', data: { offset } })
  }

  campaign(campaignId) {
    return this.fetch({ url: `/api/firmware/campaigns/${campaignId}`, method: 'get' })
  }

  // action = 'pause' | 'resume' | 'cancel' | 'release' (ignorer ce qui bloque le site `location` et continuer)
  changeCampaignStatus(campaignId, action, reason = null, location = null) {
    return this.fetch({ url: `/api/firmware/campaigns/${campaignId}/status`, method: 'post', data: { action, reason, location } })
  }

  firmware(firmwareId) {
    return this.fetch({ url: `/api/firmware/versions/${firmwareId}`, method: 'get' })
  }

  // L'aperçu en direct de la page d'un firmware : { manufacturerId, version, models, installableFrom, firmwareId }
  preview(draft) {
    return this.fetch({ url: '/api/firmware/preview', method: 'post', data: draft })
  }

  // POST (sans id) ou PUT → { id }
  saveFirmware(firmware) {
    const { id, ...body } = firmware
    return id
      ? this.fetch({ url: `/api/firmware/versions/${id}`, method: 'put', data: body })
      : this.fetch({ url: '/api/firmware/versions', method: 'post', data: body })
  }

  // → { sha256, size }
  uploadFile(firmwareId, file, sha256 = null) {
    const data = new FormData()
    data.append('file', file)
    if (sha256) data.append('sha256', sha256)
    return this.fetch({ url: `/api/firmware/versions/${firmwareId}/file`, method: 'post', data })
  }

  // withoutTests : valider sans toutes les fiches de test exigées (qui peut forcer, avec un motif)
  changeStatus(firmwareId, status, reason = null, withoutTests = false) {
    return this.fetch({ url: `/api/firmware/versions/${firmwareId}/status`, method: 'post', data: { status, reason, withoutTests } })
  }

  // Corriger la version annoncée d'un firmware publié : celle que la borne annonce fait foi
  renameVersion(firmwareId, version, reason) {
    return this.fetch({ url: `/api/firmware/versions/${firmwareId}/rename`, method: 'post', data: { version, reason } })
  }

  deleteFirmware(firmwareId) {
    return this.fetch({ url: `/api/firmware/versions/${firmwareId}`, method: 'delete' })
  }

  // Le bilan de l'épreuve d'un firmware en test, calculé maintenant
  trial(firmwareId) {
    return this.fetch({ url: `/api/firmware/versions/${firmwareId}/trial`, method: 'get' })
  }

  // Les fiches de test faites dans la version d'un firmware, la plus récente d'abord (l'ouverte relue à l'instant)
  testSheetsOfFirmware(firmwareVersionId) {
    return this.fetch({ url: '/api/firmware/test-sheets', method: 'get', data: { firmwareVersionId } })
  }

  // Éprouver un firmware sur une borne qui l'annonce : { firmwareVersionId, chargingPointIdentifier } (ou chargingPointId) → { id }
  startTestSheet({ firmwareVersionId, chargingPointIdentifier = null, chargingPointId = null }) {
    return this.fetch({ url: '/api/firmware/test-sheets', method: 'post', data: { firmwareVersionId, chargingPointIdentifier, chargingPointId } })
  }

  // Trancher une ligne à la main (state null : la rendre aux messages de la borne)
  markTestCheck(sheetId, check, state, note = null) {
    return this.fetch({ url: `/api/firmware/test-sheets/${sheetId}/checks`, method: 'put', data: { check, state, note } })
  }

  closeTestSheet(sheetId) {
    return this.fetch({ url: `/api/firmware/test-sheets/${sheetId}/close`, method: 'post' })
  }

  // Une borne seule vers un autre firmware (en test : forcé, avec un motif) → { id }
  // Figer une borne sur un firmware, ou la dégeler
  pin(chargingPointId, versionId, reason, reviewAt = null) {
    return this.fetch({ url: '/api/firmware/pins', method: 'put', data: { chargingPointId, versionId, reason, reviewAt } })
  }

  unpin(chargingPointId, reason = null) {
    return this.fetch({ url: `/api/firmware/pins/${chargingPointId}`, method: 'delete', data: { reason } })
  }

  startUpdate(chargingPointId, versionId, reason = null) {
    return this.fetch({ url: '/api/firmware/updates', method: 'post', data: { chargingPointId, versionId, reason } })
  }

  cancelUpdate(updateId, reason = null) {
    return this.fetch({ url: `/api/firmware/updates/${updateId}/cancel`, method: 'post', data: { reason } })
  }

  // Diagnostic d'une borne : { startTime?, stopTime? } → { id } (le protocole vient des réglages du constructeur)
  requestDiagnostic(chargingPointId, options = {}) {
    return this.fetch({ url: '/api/firmware/diagnostics', method: 'post', data: { chargingPointId, ...options } })
  }

  // Ceux d'une borne, ou de tout le parc
  // Par pages de 100, la plus récente d'abord
  diagnostics(chargingPointId = null, offset = 0) {
    return this.fetch({ url: '/api/firmware/diagnostics', method: 'get', data: chargingPointId ? { chargingPointId, offset } : { offset } })
  }

  // Le fichier tel que la borne l'a déposé : téléchargé dans le navigateur (le téléchargement est tracé côté serveur)
  downloadDiagnostic(diagnostic) {
    return new Promise((resolve, reject) => {
      Vue.auth.fetch({ url: `/api/firmware/diagnostics/${diagnostic.id}/file`, method: 'get', responseType: 'arraybuffer' })
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/octet-stream' }))
          const link = document.createElement('a')
          link.href = url
          link.download = diagnostic.fileName || 'diagnostics'
          link.click()
          window.URL.revokeObjectURL(url)
          resolve()
        })
        .catch(err => {
          // Le refus arrive en JSON dans un tampon : on le relit pour que l'écran puisse dire pourquoi.
          try {
            err.response.data = JSON.parse(new TextDecoder().decode(err.response.data))
          } catch (ignored) { /* pas du JSON */ }
          reject(err)
        })
    })
  }

  fetch(options) {
    return new Promise((resolve, reject) => {
      Vue.auth.fetch(options)
        .then(response => resolve(response.data))
        .catch(err => reject(err))
    })
  }
}

export default new FirmwareRepository()
